// src/components/planner/modals/AbsenceModal.tsx

import React, { useState } from 'react';
import { X, Save } from 'lucide-react';

const API_BASE_URL = '/api';

interface AbsenceModalProps {
    events: any[]; // allEvents da grade semanal
    onClose: () => void;
    onSave: () => void;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const AbsenceModal: React.FC<AbsenceModalProps> = ({ events, onClose, onSave, onShowToast }) => {
    const [formData, setFormData] = useState({
        fixedEventId: '',
        date: new Date().toISOString().split('T')[0],
        subject: '',
        reason: '',
        needsReview: false,
        customEvent: ''
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.fixedEventId) {
            onShowToast('Selecione a aula', 'error');
            return;
        }

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/absences`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    fixedEventId: formData.fixedEventId === 'outro' ? undefined : formData.fixedEventId,
                    customEventName: formData.fixedEventId === 'outro' ? formData.customEvent : undefined,
                    date: formData.date,
                    reason: formData.reason || undefined,
                    subject: formData.subject || undefined,
                    needsReview: formData.needsReview
                })
            });

            if (response.ok) {
                onShowToast('Falta registrada!', 'success');
                onSave();
            } else {
                const data = await response.json();
                onShowToast(data.error || 'Erro ao registrar falta', 'error');
            }
        } catch (error) {
            onShowToast('Erro ao registrar falta', 'error');
        }
    };

    const DAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

    return (
        <div className="plnr-modal-overlay" onClick={onClose}>
            <div className="plnr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="plnr-modal-header">
                    <h3>Registrar Falta</h3>
                    <button className="plnr-modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="plnr-modal-body">

                        {/* AULA */}
                        <div className="plnr-form-group">
                            <label>Qual aula você faltou? *</label>
                            <select
                                value={formData.fixedEventId}
                                onChange={(e) => setFormData({ ...formData, fixedEventId: e.target.value })}
                                className="plnr-select"
                                required
                            >
                                <option value="">Selecione a aula...</option>
                                {events.map(event => (
                                    <option key={event._id} value={event._id}>
                                        {event.title} — {DAY_NAMES[event.dayOfWeek]} {event.startTime?.substring(0, 5)}
                                    </option>
                                ))}
                                <option value="outro">Outro</option>
                            </select>

                            {formData.fixedEventId === 'outro' && (
                                <input
                                    type="text"
                                    value={formData.customEvent}
                                    onChange={(e) => setFormData({ ...formData, customEvent: e.target.value })}
                                    placeholder="Nome da aula que você faltou"
                                    className="plnr-input"
                                    style={{ marginTop: '8px' }}
                                    required
                                />
                            )}
                        </div>

                        {/* DATA */}
                        <div className="plnr-form-group">
                            <label>Data da aula *</label>
                            <input
                                type="date"
                                value={formData.date}
                                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                className="plnr-input"
                                required
                            />
                        </div>

                        {/* ASSUNTO */}
                        <div className="plnr-form-group">
                            <label>Assunto da aula (opcional)</label>
                            <input
                                type="text"
                                value={formData.subject}
                                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                placeholder="Ex: Hipertensão Arterial"
                                className="plnr-input"
                            />
                        </div>

                        {/* MOTIVO */}
                        <div className="plnr-form-group">
                            <label>Motivo (opcional)</label>
                            <textarea
                                value={formData.reason}
                                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                                placeholder="Ex: Consulta médica, viagem..."
                                className="plnr-textarea"
                                rows={2}
                            />
                        </div>

                        {/* PRECISA REVISAR */}
                        <div className="plnr-absence-check">
                            <label className="plnr-absence-check-label">
                                <input
                                    type="checkbox"
                                    checked={formData.needsReview}
                                    onChange={(e) => setFormData({ ...formData, needsReview: e.target.checked })}
                                    className="plnr-absence-checkbox"
                                />
                                <span>Preciso revisar o conteúdo desta aula</span>
                            </label>
                        </div>

                    </div>

                    <div className="plnr-modal-footer">
                        <button type="button" className="plnr-btn secondary" onClick={onClose}>
                            Cancelar
                        </button>
                        <button type="submit" className="plnr-btn primary">
                            <Save size={16} />
                            Registrar Falta
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AbsenceModal;